import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronLeft, ChevronRight, Info, MoreVertical, X } from "lucide-react";
import DatePicker from "../../../Common/DatePicker";
import {
  resolveEmployeeMinDate,
  resolveEmployeeMinMonthValue,
} from "../../../../utils/selectableDateBounds";
import { DsBadge, DsIcon } from "../../../../design-system";
import { attendanceService, authService } from "../../../../services/api";
import { useAttendanceCodeConfig } from "../../../../context/attendanceCodeConfigContext";
import { usePermissions } from "../../../../rbac/PermissionsContext";
import {
  buildMonthPolicyStatusMap,
  resolveEmployeeStatusEditAccess,
} from "../../../../lib/attendancePolicyEngine";
import { SHIFT_OVERRIDE_UPDATED_EVENT } from "../../../../lib/attendanceShiftOverride";
import toast from "react-hot-toast";
import {
  ATTENDANCE_POLICY_UPDATED_EVENT,
  getDemoPolicyTestGuide,
} from "../../../../lib/attendanceDemoSeed";
import {
  ATTENDANCE_PUNCH_UPDATED_EVENT,
  formatDurationHms,
  hasLocalCheckoutGuard,
  readEmployeeWorklog,
  readEmployeeWorklogMap,
  workedMsFromPunchRows,
  workedMsFromSessions,
} from "../../../../lib/attendancePunchStorage";
import { ATTENDANCE_SCHEDULE_RECALC_EVENT } from "../../../../lib/attendanceGeneralSettings";
import {
  clampDateIsoToToday,
  isFutureDateIso,
  regularisedChecksSessionKey,
  formatPunchDateTimeDisplay,
  resolveDayDetailHours,
} from "../../../../lib/attendanceDayChecks";
import { getDayOfMonthSafe, parseYearMonthDay } from "../../../../lib/attendanceDayDefaults";
import {
  sandwichAppliedFromRecord,
  sandwichUnpaidWoCount,
} from "../../../../lib/attendanceSandwich";
import { statusToMarkKind } from "../../../../lib/essHomeHelpers";
import { getAuthJson } from "../../../../utils/authStorage";
import { resolveEmployeeAttendanceRequestAccess } from "../../../../lib/attendanceRequestAccess";
import {
  allowEssQuickPanelScreen,
  DAY_ACTION_QUICK_PANEL_KEY,
} from "../../../../lib/essQuickPanelRequestAccess";
import { StatusDayCell } from "../../../shared/StatusMonthCalendar";
import { employeeHasRestrictedHolidayOption } from "../../../../lib/restrictedHolidayAccess";
import { submitRegulariseRequest } from "../../../../lib/attendanceDeskRequests";
import RegulariseChecksRequestModal from "./RegulariseChecksRequestModal";
import { isAttendanceMonthLocked } from "../../../../lib/attendanceMonthLock";
import { useIsPhone } from "../../../../hooks/useIsPhone";
import MobileSheet from "../../shell/MobileSheet";
import {
  PHONE_SHEET_BACKDROP_CLS,
  PHONE_SHEET_PANEL_CLS,
  usePhoneFormBack,
} from "./RequestHistoryOverlay";

const getMonthRangeValue = (year, month) => {
  const paddedMonth = (month + 1).toString().padStart(2, "0");
  const endDay = new Date(year, month + 1, 0).getDate().toString().padStart(2, "0");
  return `${year}-${paddedMonth}-01/${year}-${paddedMonth}-${endDay}`;
};

const combineStatuses = (firstHalfStatus, secondHalfStatus) => {
  if (firstHalfStatus === secondHalfStatus) return firstHalfStatus;
  return `${firstHalfStatus}/${secondHalfStatus}`;
};

const normalizeStatus = (status) => {
  const s = String(status || "").trim().toUpperCase();
  if (s === "LWP" || s === "LP") return "LOP";
  return s;
};

/** Split full-day / half-day attendance codes for month status counting. */
const splitAttendanceStatusParts = (raw, knownCodes) => {
  const status = String(raw || "").trim().toUpperCase();
  if (!status || status === "-") return [];
  if (status.includes("/")) {
    return status
      .split("/")
      .map((p) => normalizeStatus(p.trim()))
      .filter((p) => p && p !== "-");
  }
  const known = knownCodes instanceof Set ? knownCodes : new Set();
  if (status.length <= 3 && (known.has(status) || known.has(normalizeStatus(status)))) {
    return [normalizeStatus(status)];
  }
  for (let len = 3; len >= 2; len--) {
    const first = status.substring(0, len);
    const rest = status.substring(len);
    if (!rest) continue;
    const nFirst = normalizeStatus(first);
    const nRest = normalizeStatus(rest);
    if (
      (known.has(first) || known.has(nFirst)) &&
      (known.has(rest) || known.has(nRest))
    ) {
      return [nFirst, nRest];
    }
  }
  return [normalizeStatus(status)];
};

const formatStatusCount = (value) => {
  const n = Number(value) || 0;
  if (Number.isInteger(n)) return String(n);
  return String(Math.round(n * 10) / 10);
};

/** Map canonical codes to company display codes (matches Attendance grid). */
const mapStatusForDisplay = (raw, attendanceMapping) => {
  if (!raw || typeof raw !== "string") return raw;
  const s = raw.trim();
  if (!s) return raw;
  if (s.includes("/")) {
    const [a, b] = s.split("/");
    const ma =
      attendanceMapping?.[String(a).toUpperCase().trim()] || String(a).trim();
    const mb =
      attendanceMapping?.[String(b).toUpperCase().trim()] || String(b).trim();
    return `${ma}/${mb}`;
  }
  const lookup = s.toUpperCase();
  return attendanceMapping?.[lookup] || s;
};

const splitStatusParts = (displayStr) => {
  if (!displayStr || typeof displayStr !== "string") return null;
  const s = displayStr.trim();
  if (s.includes("/")) {
    const p = s.split("/");
    if (p.length === 2 && p[0].trim()) {
      return [p[0].trim(), p[1].trim() || "-"];
    }
  }
  return null;
};

/**
 * Color rules aligned with AttendanceTable / AttendanceCell in Attendance.jsx
 * (custom colors from leave types + heuristic fallbacks).
 */
const getStatusColorPresentation = (code, codeColorMap, darkMode) => {
  if (!code || typeof code !== "string") {
    return {
      className: darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-fg))]",
      style: {},
    };
  }
  const norm = normalizeStatus(code).toUpperCase();
  if (codeColorMap && codeColorMap[norm]) {
    const color = codeColorMap[norm];
    if (
      color &&
      (color.startsWith("#") || color.startsWith("rgb") || /^[a-zA-Z]+$/.test(color))
    ) {
      return { className: "font-bold", style: { color } };
    }
    return { className: `${color} font-bold`, style: {} };
  }
  if (
    norm.includes("LOP") ||
    norm.includes("LWP") ||
    norm.includes("LO") ||
    norm.includes("LW")
  ) {
    return {
      className: darkMode ? "text-red-400 font-bold" : "text-red-700 font-bold",
      style: {},
    };
  }
  const light = {
    PR: "text-emerald-700",
    PH: "text-amber-700",
    WO: "text-[hsl(var(--ds-secondary))]",
    LOP: "text-red-700",
  };
  const dark = {
    PR: "text-emerald-400",
    PH: "text-amber-400",
    WO: "text-cyan-300",
    LOP: "text-red-400",
  };
  const palette = darkMode ? dark : light;
  return {
    className: `${palette[norm] || (darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-fg))]")} font-bold`,
    style: {},
  };
};

const ATTENDANCE_SHIFTS_STORAGE_KEY = "hrms_frontend_attendance_shifts_v1";
const POLICY_REFRESH_MS = 60000;
const QUICK_ACTION_REQUESTS_STORAGE_KEY = "adminQuickActionRequests";

const resolveDefaultShiftName = () => {
  try {
    const raw = localStorage.getItem(ATTENDANCE_SHIFTS_STORAGE_KEY);
    if (!raw) return "General";
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return "General";
    const defaultShift = parsed.find((s) => s && s.isDefault);
    if (defaultShift?.name) return String(defaultShift.name).trim();
    const generalShift = parsed.find(
      (s) => s && String(s.name || "").trim().toLowerCase() === "general"
    );
    if (generalShift?.name) return String(generalShift.name).trim();
    return "General";
  } catch {
    return "General";
  }
};

const resolveApprovedChangeShiftName = (employeeId, selectedDateIso) => {
  if (!employeeId || !selectedDateIso) return null;
  try {
    const raw = localStorage.getItem(QUICK_ACTION_REQUESTS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;

    const targetEmpId = Number(employeeId);
    const approvedShiftRequests = parsed
      .filter((r) => {
        if (!r || r.actionLabel !== "Change Shift" || r.status !== "Approved") return false;
        const reqEmpId = Number(r.employeeId);
        if (!Number.isFinite(reqEmpId) || !Number.isFinite(targetEmpId) || reqEmpId !== targetEmpId) {
          return false;
        }
        const effectiveDate = String(r?.formData?.date || "").trim();
        const shiftName = String(r?.formData?.shift || "").trim();
        if (!effectiveDate || !shiftName) return false;
        return effectiveDate <= selectedDateIso;
      })
      .sort((a, b) => {
        const ad = String(a?.formData?.date || "");
        const bd = String(b?.formData?.date || "");
        if (ad !== bd) return bd.localeCompare(ad);
        return Number(b?.submittedAt || 0) - Number(a?.submittedAt || 0);
      });

    if (approvedShiftRequests.length === 0) return null;
    return String(approvedShiftRequests[0]?.formData?.shift || "").trim() || null;
  } catch {
    return null;
  }
};

const getDefaultSelectedDay = (year, month) => {
  const now = new Date();
  if (year === now.getFullYear() && month === now.getMonth()) {
    return now.getDate();
  }
  return null;
};

const renderColoredStatusContent = (
  raw,
  attendanceMapping,
  codeColorMap,
  darkMode,
  textSizeClass = "text-[9.5px]"
) => {
  if (!raw || raw === "-") {
    return (
      <span
        className={`${textSizeClass} font-bold ${
          darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
        }`}
      >
        -
      </span>
    );
  }
  const mapped = mapStatusForDisplay(String(raw), attendanceMapping);
  const parts = splitStatusParts(mapped);
  if (parts) {
    return (
      <div className="flex flex-col items-center justify-center gap-0 leading-none">
        {parts.map((p, i) => {
          const pres = getStatusColorPresentation(p, codeColorMap, darkMode);
          return (
            <span key={`${p}-${i}`} className="flex flex-col items-center">
              {i > 0 ? (
                <span
                  className={`${textSizeClass} font-semibold ${
                    darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
                  }`}
                  aria-hidden
                >
                  |
                </span>
              ) : null}
              <span className={`${textSizeClass} ${pres.className}`} style={pres.style}>
                {p}
              </span>
            </span>
          );
        })}
      </div>
    );
  }
  const pres = getStatusColorPresentation(mapped, codeColorMap, darkMode);
  return (
    <span className={`${textSizeClass} ${pres.className}`} style={pres.style}>
      {mapped}
    </span>
  );
};

const EmployeeAttendanceCalendarPopup = ({
  darkMode,
  isOpen,
  onClose,
  embedded = false,
  mode = "auto",
  employee,
  selectedMonth,
  selectedYear,
  attendanceData = {},
  attendanceDetailData = {},
  leaveTypes = [],
  isLocked = false,
  onApplyLeave,
  onChangeShift = null,
  onChangeWo = null,
  onShortLeave = null,
  onOdRequest = null,
  onCompOffRequest = null,
  onOtRequest = null,
  onRhRequest = null,
  onLeaveBalance = null,
  onCompOffLedger = null,
  onEditAttendanceStatus,
  onSaveRegularizationTimes,
  /** Persist pending calendar edits into attendance save (Override / Auto-present). */
  onPersistMonth = null,
  onViewEditChecks = null,
  canViewEditChecks = false,
  viewChecksReadOnly = false,
  weeklyOffPolicies = [],
  employeeWeeklyOffMappings = {},
  holidaysData = [],
  holidayPolicies = [],
  employeeHolidayMappings = {},
  /** When true: Process eye view — no day actions / Save; month locked; show month totals. */
  summaryMode = false,
  /**
   * When true (Process Attendance Change Status / Process eye): keep calendar on the
   * attendance month being processed — no prev/next month or month picker.
   */
  lockMonthNavigation = false,
  /** Optional month totals for summaryMode: { totalDays, payDays, lopReversal } */
  monthSummary = null,
  /**
   * Show month status counts + payable days.
   * Process Attendance: pass true while processing.
   * ESS / Register: pass true only when the month is finalised (locked).
   */
  showProcessStatusSummary = false,
  /** When true, show a Finalised badge above the status summary (ESS / post-lock views). */
  showFinalisedBadge = false,
  getDayDefault = null,
  /** Optional queue for Change Status: navigate Previous / Next among selected employees only */
  employeeQueue = null,
  employeeQueueIndex = 0,
  onEmployeeQueueNavigate = null,
  /**
   * When true (ESS Attendance Logs): day ⋮ raise actions also require matching
   * Quick Panel screen view. Admin / My Team leave this false (policy + callbacks only).
   */
  requireQuickPanelForDayActions = false,
  /**
   * Admin Change Status: hide “Actions for selected day” (⋮ + tip).
   * Those actions are on Process → Actions. Leave false for ESS (embedded calendar).
   */
  hideSelectedDayActions = false,
  /** Attendance month locks (finalised months). Used for viewed-month payable counts and edit block. */
  attendanceLocks = null,
}) => {
  const isManualMode = mode === "manual";
  const isSummaryMode = !!summaryMode;
  const isMonthLocked = isSummaryMode || !!lockMonthNavigation;
  const { mapping: attendanceMapping } = useAttendanceCodeConfig();
  const { hasScreenAccess, isAdmin } = usePermissions();
  const qpOk = (screenKey) =>
    !requireQuickPanelForDayActions ||
    allowEssQuickPanelScreen(hasScreenAccess, isAdmin, screenKey);
  const [selectedDay, setSelectedDay] = useState(null);
  const [localMonth, setLocalMonth] = useState(selectedMonth);
  const [localYear, setLocalYear] = useState(selectedYear);
  const viewedMonthLocked = Array.isArray(attendanceLocks)
    ? isAttendanceMonthLocked(attendanceLocks, localMonth, localYear)
    : !!isLocked;
  const showProcessSummary = !!showProcessStatusSummary || viewedMonthLocked;
  const showFinalisedMark = !!showFinalisedBadge || viewedMonthLocked;
  const [localAttendanceMap, setLocalAttendanceMap] = useState({});
  const [localAttendanceDetailMap, setLocalAttendanceDetailMap] = useState({});
  const [isLoadingMonthData, setIsLoadingMonthData] = useState(false);
  const [showDayActionMenu, setShowDayActionMenu] = useState(false);
  const [dayActionMenuCoords, setDayActionMenuCoords] = useState({ top: 0, left: 0 });
  const dayActionBtnRef = useRef(null);
  const [showDayActionsInfo, setShowDayActionsInfo] = useState(false);
  const [dayActionsInfoCoords, setDayActionsInfoCoords] = useState({ top: 0, left: 0 });
  const dayActionsInfoBtnRef = useRef(null);
  const [showMonthSummaryInfo, setShowMonthSummaryInfo] = useState(false);
  const [monthSummaryInfoCoords, setMonthSummaryInfoCoords] = useState({ top: 0, left: 0 });
  const monthSummaryInfoBtnRef = useRef(null);
  const [showStatusEditModal, setShowStatusEditModal] = useState(false);
  const [isSavingStatus, setIsSavingStatus] = useState(false);
  const [statusEditHalfDayMode, setStatusEditHalfDayMode] = useState(false);
  const [firstHalfStatus, setFirstHalfStatus] = useState("PR");
  const [secondHalfStatus, setSecondHalfStatus] = useState("PR");
  const dayClickTimerRef = useRef(null);
  const [showRegularizationModal, setShowRegularizationModal] = useState(false);
  const [regularisationSessionLog, setRegularisationSessionLog] = useState([]);
  const [policyRefreshTick, setPolicyRefreshTick] = useState(0);
  const [hoursTick, setHoursTick] = useState(0);
  const [loggedInEmployeeId, setLoggedInEmployeeId] = useState(null);
  const [demoPolicyReady, setDemoPolicyReady] = useState(false);
  // Phone layout applies to the employee page only; the admin popup is unchanged on phones.
  const phoneEss = useIsPhone() && embedded;
  const [phoneDaySheet, setPhoneDaySheet] = useState(false);
  usePhoneFormBack(embedded && showStatusEditModal, () => setShowStatusEditModal(false));

  const requestAccess = useMemo(
    () => resolveEmployeeAttendanceRequestAccess(employee),
    [employee, policyRefreshTick]
  );

  const employeeHasRhDates = useMemo(
    () =>
      employeeHasRestrictedHolidayOption({
        employeeId: employee?.id,
        holidayPolicies,
        employeeHolidayMappings,
      }),
    [employee?.id, holidayPolicies, employeeHolidayMappings]
  );

  // ESS self-service: policy ∧ Quick Panel. Admin/My Team: policy + callbacks only.
  const canApplyLeave =
    !isSummaryMode &&
    !!requestAccess.leave &&
    typeof onApplyLeave === "function" &&
    qpOk(DAY_ACTION_QUICK_PANEL_KEY.leave);
  const canLeaveBalance =
    !isSummaryMode && typeof onLeaveBalance === "function";
  const canCompOffLedger =
    !isSummaryMode && typeof onCompOffLedger === "function";
  const canRegularise =
    !isSummaryMode &&
    !isManualMode &&
    !!requestAccess.regularise &&
    qpOk(DAY_ACTION_QUICK_PANEL_KEY.regularise);
  const canOpenViewChecks =
    !isSummaryMode &&
    typeof onViewEditChecks === "function" &&
    (canViewEditChecks || viewChecksReadOnly || embedded);
  const canChangeShift =
    !isSummaryMode &&
    !!requestAccess.changeShift &&
    typeof onChangeShift === "function" &&
    qpOk(DAY_ACTION_QUICK_PANEL_KEY.changeShift);
  const canChangeWo =
    !isSummaryMode &&
    !!requestAccess.changeWo &&
    typeof onChangeWo === "function" &&
    qpOk(DAY_ACTION_QUICK_PANEL_KEY.changeWo);
  const canShortLeave =
    !isSummaryMode &&
    !!requestAccess.shortLeave &&
    typeof onShortLeave === "function" &&
    qpOk(DAY_ACTION_QUICK_PANEL_KEY.shortLeave);
  const canOd =
    !isSummaryMode &&
    !!requestAccess.od &&
    typeof onOdRequest === "function" &&
    qpOk(DAY_ACTION_QUICK_PANEL_KEY.od);
  const canCompOff =
    !isSummaryMode &&
    !requireQuickPanelForDayActions &&
    !!requestAccess.compOff &&
    typeof onCompOffRequest === "function" &&
    qpOk(DAY_ACTION_QUICK_PANEL_KEY.compOff);
  const canOt =
    !isSummaryMode &&
    !!requestAccess.ot &&
    typeof onOtRequest === "function" &&
    qpOk(DAY_ACTION_QUICK_PANEL_KEY.ot);
  const canRh =
    !isSummaryMode &&
    typeof onRhRequest === "function" &&
    (qpOk(DAY_ACTION_QUICK_PANEL_KEY.restrictedHoliday) ||
      employeeHasRhDates ||
      isAdmin);

  const regLogStorageKey = useMemo(
    () => regularisedChecksSessionKey(employee?.id),
    [employee?.id]
  );

  const demoTestGuide = useMemo(() => getDemoPolicyTestGuide(), []);

  const reloadRegularisationSessionLog = () => {
    if (!regLogStorageKey) return;
    try {
      const raw = sessionStorage.getItem(regLogStorageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setRegularisationSessionLog(parsed);
      } else {
        setRegularisationSessionLog([]);
      }
    } catch {
      setRegularisationSessionLog([]);
    }
  };

  useEffect(() => {
    if (!isOpen || !regLogStorageKey) return;
    reloadRegularisationSessionLog();
  }, [isOpen, regLogStorageKey]);

  useLayoutEffect(() => {
    if (!showDayActionMenu) return;
    const btn = dayActionBtnRef.current;
    if (!btn) return;
    const placeBelow = () => {
      const rect = btn.getBoundingClientRect();
      const menuWidth = 176;
      const margin = 8;
      const maxH = Math.min(Math.round(window.innerHeight * 0.5), 260);
      let left = rect.right - menuWidth;
      left = Math.max(margin, Math.min(left, window.innerWidth - menuWidth - margin));
      let top = rect.bottom + 4;
      if (top + 120 > window.innerHeight - margin) {
        top = Math.max(margin, rect.top - maxH - 4);
      }
      setDayActionMenuCoords({ top, left, maxHeight: maxH });
    };
    placeBelow();
    window.addEventListener("resize", placeBelow);
    window.addEventListener("scroll", placeBelow, true);
    return () => {
      window.removeEventListener("resize", placeBelow);
      window.removeEventListener("scroll", placeBelow, true);
    };
  }, [showDayActionMenu, selectedDay]);

  useEffect(() => {
    if (!showDayActionMenu) return;
    const onDown = (e) => {
      if (dayActionBtnRef.current?.contains(e.target)) return;
      if (e.target.closest("[data-day-action-menu]")) return;
      setShowDayActionMenu(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [showDayActionMenu]);

  useLayoutEffect(() => {
    if (!showDayActionsInfo) return;
    const btn = dayActionsInfoBtnRef.current;
    if (!btn) return;
    const place = () => {
      const rect = btn.getBoundingClientRect();
      const menuWidth = 288;
      const margin = 8;
      const maxH = Math.min(Math.round(window.innerHeight * 0.4), 220);
      let left = rect.left;
      left = Math.max(margin, Math.min(left, window.innerWidth - menuWidth - margin));
      let top = rect.bottom + 4;
      if (top + 80 > window.innerHeight - margin) {
        top = Math.max(margin, rect.top - maxH - 4);
      }
      setDayActionsInfoCoords({ top, left, maxHeight: maxH, width: menuWidth });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [showDayActionsInfo]);

  useEffect(() => {
    if (!showDayActionsInfo) return;
    const onDown = (e) => {
      if (dayActionsInfoBtnRef.current?.contains(e.target)) return;
      if (e.target.closest("[data-day-actions-info]")) return;
      setShowDayActionsInfo(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [showDayActionsInfo]);

  useLayoutEffect(() => {
    if (!showMonthSummaryInfo) return;
    const btn = monthSummaryInfoBtnRef.current;
    if (!btn) return;
    const place = () => {
      const rect = btn.getBoundingClientRect();
      const menuWidth = 288;
      const margin = 8;
      const maxH = Math.min(Math.round(window.innerHeight * 0.4), 220);
      let left = rect.left;
      left = Math.max(margin, Math.min(left, window.innerWidth - menuWidth - margin));
      let top = rect.bottom + 4;
      if (top + 80 > window.innerHeight - margin) {
        top = Math.max(margin, rect.top - maxH - 4);
      }
      setMonthSummaryInfoCoords({ top, left, maxHeight: maxH, width: menuWidth });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [showMonthSummaryInfo]);

  useEffect(() => {
    if (!showMonthSummaryInfo) return;
    const onDown = (e) => {
      if (monthSummaryInfoBtnRef.current?.contains(e.target)) return;
      if (e.target.closest("[data-month-summary-info]")) return;
      setShowMonthSummaryInfo(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [showMonthSummaryInfo]);

  useEffect(() => {
    setShowDayActionMenu(false);
    setShowDayActionsInfo(false);
    setShowMonthSummaryInfo(false);
  }, [selectedDay, localMonth, localYear, employee?.id]);

  useEffect(() => {
    if (!isOpen || !employee?.id) return;
    const onHistory = (e) => {
      const id = String(e?.detail?.employeeId ?? "");
      if (!id || id === String(employee.id)) reloadRegularisationSessionLog();
    };
    window.addEventListener("hrms-regularisation-history-updated", onHistory);
    return () => window.removeEventListener("hrms-regularisation-history-updated", onHistory);
  }, [isOpen, employee?.id, regLogStorageKey]);

  useEffect(() => {
    if (!isOpen) return;
    // Demo policy is no longer auto-reseeded on calendar open.
    setDemoPolicyReady(false);
    let cancelled = false;
    authService
      .getCurrentUser()
      .then((res) => {
        if (cancelled) return;
        const id = res?.data?.employeeId ?? res?.data?.employee?.id ?? null;
        setLoggedInEmployeeId(id != null ? String(id) : null);
      })
      .catch(() => {
        if (!cancelled) setLoggedInEmployeeId(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  /** Refresh PR/LOP when punches change, policy changes, or on interval. */
  useEffect(() => {
    if (!isOpen) return;
    const bump = () => setPolicyRefreshTick((n) => n + 1);
    const onPunch = (e) => {
      const targetId = String(employee?.id ?? "");
      const punchEmp = String(e?.detail?.employeeId ?? "");
      if (!targetId || punchEmp === targetId) bump();
    };
    window.addEventListener(ATTENDANCE_PUNCH_UPDATED_EVENT, onPunch);
    window.addEventListener(ATTENDANCE_POLICY_UPDATED_EVENT, bump);
    window.addEventListener(ATTENDANCE_SCHEDULE_RECALC_EVENT, bump);
    window.addEventListener(SHIFT_OVERRIDE_UPDATED_EVENT, bump);
    const intervalId = window.setInterval(bump, POLICY_REFRESH_MS);
    return () => {
      window.removeEventListener(ATTENDANCE_PUNCH_UPDATED_EVENT, onPunch);
      window.removeEventListener(ATTENDANCE_POLICY_UPDATED_EVENT, bump);
      window.removeEventListener(ATTENDANCE_SCHEDULE_RECALC_EVENT, bump);
      window.removeEventListener(SHIFT_OVERRIDE_UPDATED_EVENT, bump);
      window.clearInterval(intervalId);
    };
  }, [isOpen, employee?.id]);

  useEffect(() => {
    if (!isOpen && !embedded) return;
    const id = window.setInterval(() => setHoursTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [isOpen, embedded]);

  /** Reset calendar UI when month/employee changes (history lives in RegulariseChecksRequestModal). */
  useEffect(() => {
    if (!isOpen) return;
    const now = new Date();
    let y = selectedYear;
    let mo = selectedMonth;
    // When month is locked to the process month, never clamp away from it.
    if (
      !isMonthLocked &&
      (y > now.getFullYear() || (y === now.getFullYear() && mo > now.getMonth()))
    ) {
      y = now.getFullYear();
      mo = now.getMonth();
    }
    setSelectedDay(getDefaultSelectedDay(y, mo));
    setLocalMonth(mo);
    setLocalYear(y);
    setShowDayActionMenu(false);
    setShowRegularizationModal(false);
  }, [isOpen, selectedMonth, selectedYear, employee?.id, isMonthLocked]);

  const daysInMonth = useMemo(
    () => Math.min(31, new Date(localYear, localMonth + 1, 0).getDate()),
    [localYear, localMonth]
  );

  /** JS getDay() is Sun=0; shift so week starts Monday (Mon=0 … Sun=6). */
  const dayOffset = useMemo(
    () => (new Date(localYear, localMonth, 1).getDay() + 6) % 7,
    [localYear, localMonth]
  );

  const employeeId = String(employee?.id ?? "");
  const isLocalCurrentMonth = localMonth === selectedMonth && localYear === selectedYear;
  const [localMonthSynced, setLocalMonthSynced] = useState(false);
  // While loading, do NOT use parent Process grid cache (lightweight → policy paints LP flash).
  // Only paint after this employee/month API reload finishes.
  const empStatusMap = localMonthSynced ? localAttendanceMap : {};
  const empDetailMap = localMonthSynced ? localAttendanceDetailMap : {};
  const showCalendarLoading = Boolean(isOpen && employee?.id && !localMonthSynced);

  const employeeWorklogByDay = useMemo(() => {
    if (!employee?.id) return {};
    return readEmployeeWorklogMap(employee.id);
  }, [employee?.id, policyRefreshTick]);

  const displayStatusMap = useMemo(() => {
    if (!employee?.id) return empStatusMap;
    // Hold paint until API month sync — empty map + autoApply would flash all LP.
    if (!localMonthSynced) return {};
    const resolveDefault =
      typeof getDayDefault === "function"
        ? getDayDefault
        : (date, emp) => {
            void emp;
            void weeklyOffPolicies;
            void employeeWeeklyOffMappings;
            void holidaysData;
            void holidayPolicies;
            void employeeHolidayMappings;
            // Fallback if parent did not pass Settings resolver
            return isManualMode ? "PR" : "LOP";
          };
    const map = buildMonthPolicyStatusMap({
      employee,
      year: localYear,
      month: localMonth,
      statusMap: empStatusMap,
      detailMap: empDetailMap,
      worklogByDay: employeeWorklogByDay,
      daysInMonth,
      loggedInEmployeeId,
      autoApplyForAllEmployees: true,
      getDayDefault: resolveDefault,
    });

    // Outside DOJ→LWD: paint blank only (never WO/LP/LOP from policy filler).
    let joinDateNum = 0;
    let relievedDateNum = 99999999;
    if (employee?.joiningDate || employee?.joinDate) {
      const d = new Date(employee.joiningDate || employee.joinDate);
      if (!Number.isNaN(d.getTime())) {
        joinDateNum = d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
      }
    }
    const relieved =
      employee?.relievedDate || employee?.exitDate || employee?.relievingDate;
    if (relieved) {
      const d = new Date(relieved);
      if (!Number.isNaN(d.getTime())) {
        relievedDateNum = d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
      }
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const currentNum = localYear * 10000 + (localMonth + 1) * 100 + day;
      if (currentNum < joinDateNum || currentNum > relievedDateNum) {
        map[String(day)] = "-";
        map[day] = "-";
      }
    }
    return map;
  }, [
    employee,
    localYear,
    localMonth,
    localMonthSynced,
    empStatusMap,
    empDetailMap,
    employeeWorklogByDay,
    daysInMonth,
    policyRefreshTick,
    loggedInEmployeeId,
    demoPolicyReady,
    getDayDefault,
    isManualMode,
    weeklyOffPolicies,
    employeeWeeklyOffMappings,
    holidaysData,
    holidayPolicies,
    employeeHolidayMappings,
  ]);
  const statusOptions = useMemo(() => {
    const map = new Map();
    [
      ["PR", "Present"],
      ["LOP", "Absent"],
      ["WO", "Weekly Off"],
      ["PH", "Public Holiday"],
    ].forEach(([code, label]) => {
      const mappedCode = attendanceMapping?.[code] || code;
      map.set(mappedCode, label);
    });
    (leaveTypes || []).forEach((lt) => {
      if (!lt || lt.isActive === false) return;
      const rawCode = (lt.code || lt.shortCode || lt.id || "").toString().toUpperCase();
      if (!rawCode) return;
      const mappedCode = attendanceMapping?.[rawCode] || rawCode;
      if (!map.has(mappedCode)) {
        map.set(mappedCode, lt.name || lt.leaveName || rawCode);
      }
    });
    return Array.from(map.entries()).map(([code, label]) => ({ code, label }));
  }, [attendanceMapping, leaveTypes]);

  /** Map painted/API status → dropdown option code (company mapping, e.g. LOP → LP). */
  const toStatusOptionCode = (raw) => {
    let s = String(raw || "").trim().toUpperCase();
    if (!s || s === "-" || s === "--") return "";
    if (s === "LP" || s === "LWP" || s === "LO") s = "LOP";
    if (s === "PRESENT") s = "PR";
    const mapped = String(attendanceMapping?.[s] || s).trim().toUpperCase();
    const optionCodes = new Set((statusOptions || []).map((o) => String(o.code || "").toUpperCase()));
    if (optionCodes.has(mapped)) return mapped;
    if (optionCodes.has(s)) return s;
    const rawU = String(raw || "").trim().toUpperCase();
    if (optionCodes.has(rawU)) return rawU;
    return mapped || s;
  };

  const codeColorMap = useMemo(() => {
    const m = {};
    (leaveTypes || []).forEach((lt) => {
      const code = String(lt.code || lt.shortCode || lt.id || "").toUpperCase();
      if (code && lt.color) m[code] = lt.color;
    });
    return m;
  }, [leaveTypes]);

  // Always treat LOP / LP / LWP as unpaid for payable-day math.
  const unpaidLeaveCodes = useMemo(() => {
    const set = new Set(["LOP", "LP", "LWP"]);
    (leaveTypes || []).forEach((lt) => {
      const unpaid =
        lt?.isUnpaid === true ||
        lt?.isPaid === false ||
        lt?.isPaid === 0 ||
        String(lt?.isPaid).toLowerCase() === "false";
      if (!lt || !unpaid) return;
      const raw = String(lt.code || lt.shortCode || lt.id || "").trim().toUpperCase();
      if (raw) set.add(normalizeStatus(raw));
    });
    return set;
  }, [leaveTypes]);

  const paidLeaveCodes = useMemo(() => {
    const set = new Set(["PR", "PH", "WO"]);
    (leaveTypes || []).forEach((lt) => {
      if (!lt || lt.isUnpaid === true) return;
      const raw = String(lt.code || lt.shortCode || lt.id || "").trim().toUpperCase();
      if (raw) set.add(normalizeStatus(raw));
    });
    return set;
  }, [leaveTypes]);

  const knownStatusCodes = useMemo(() => {
    const set = new Set(["PR", "PH", "WO", "LOP", "LP", "LWP", "CL", "EL", "CO", "CW"]);
    (leaveTypes || []).forEach((lt) => {
      const raw = String(lt.code || lt.shortCode || lt.id || "").trim().toUpperCase();
      if (raw) {
        set.add(raw);
        set.add(normalizeStatus(raw));
      }
    });
    Object.entries(attendanceMapping || {}).forEach(([canonical, display]) => {
      const c = String(canonical || "").trim().toUpperCase();
      const d = String(display || "").trim().toUpperCase();
      if (c) set.add(c);
      if (d) set.add(d);
    });
    return set;
  }, [leaveTypes, attendanceMapping]);

  /** Process Attendance calendar: status counts + payable days from visible month map. */
  const processMonthStatusSummary = useMemo(() => {
    if (!showProcessSummary) return null;
    if (!localMonthSynced) return null;
    const counts = {};
    let joinDateNum = 0;
    let relievedDateNum = 99999999;
    if (employee?.joiningDate || employee?.joinDate) {
      const d = new Date(employee.joiningDate || employee.joinDate);
      if (!Number.isNaN(d.getTime())) {
        joinDateNum = d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
      }
    }
    const relieved =
      employee?.relievedDate || employee?.exitDate || employee?.relievingDate;
    if (relieved) {
      const d = new Date(relieved);
      if (!Number.isNaN(d.getTime())) {
        relievedDateNum = d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
      }
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const currentNum = localYear * 10000 + (localMonth + 1) * 100 + day;
      if (currentNum < joinDateNum || currentNum > relievedDateNum) continue;
      const raw = displayStatusMap?.[day] ?? displayStatusMap?.[String(day)];
      const parts = splitAttendanceStatusParts(raw, knownStatusCodes);
      if (!parts.length) continue;
      const weight = parts.length >= 2 ? 0.5 : 1;
      parts.forEach((part) => {
        const key = normalizeStatus(part);
        if (!key || key === "-") return;
        counts[key] = (counts[key] || 0) + weight;
      });
    }
    let payDays = 0;
    Object.entries(counts).forEach(([code, val]) => {
      const c = Number(val) || 0;
      if (c === 0) return;
      const upper = normalizeStatus(code);
      if (unpaidLeaveCodes.has(upper) || upper === "LOP") return;
      if (paidLeaveCodes.has(upper)) payDays += c;
      else payDays += c;
    });
    payDays -= sandwichUnpaidWoCount(
      localAttendanceDetailMap,
      displayStatusMap,
      daysInMonth
    );
    payDays = Math.max(0, payDays);
    const preferredOrder = ["PR", "PH", "WO"];
    const leaveOrder = (leaveTypes || [])
      .filter((lt) => lt && lt.isActive !== false)
      .map((lt) => normalizeStatus(String(lt.code || lt.shortCode || lt.id || "").trim()))
      .filter(Boolean);
    const orderedKeys = [];
    const seen = new Set();
    [...preferredOrder, ...leaveOrder, "LOP"].forEach((k) => {
      if (!k || seen.has(k)) return;
      seen.add(k);
      orderedKeys.push(k);
    });
    Object.keys(counts)
      .sort()
      .forEach((k) => {
        if (!seen.has(k)) {
          seen.add(k);
          orderedKeys.push(k);
        }
      });
    const entries = orderedKeys
      .filter((k) => (counts[k] || 0) > 0)
      .map((k) => ({
        code: k,
        displayCode: mapStatusForDisplay(k, attendanceMapping) || k,
        count: counts[k] || 0,
      }));
    return { entries, payDays, counts };
  }, [
    showProcessSummary,
    localMonthSynced,
    displayStatusMap,
    localAttendanceDetailMap,
    daysInMonth,
    localYear,
    localMonth,
    employee,
    knownStatusCodes,
    unpaidLeaveCodes,
    paidLeaveCodes,
    leaveTypes,
    attendanceMapping,
  ]);

  useEffect(() => {
    const fetchEmployeeMonthAttendance = async () => {
      if (!isOpen || !employee?.id) {
        setLocalMonthSynced(false);
        return;
      }
      // Always reload this employee/month from API so WO/PH (and other policy
      // heals) appear on open/refresh — do not rely on stale parent grid cache
      // or when Process / Reprocess / Unprocess runs the attendance engine sync.
      try {
        setLocalMonthSynced(false);
        setLocalAttendanceMap({});
        setLocalAttendanceDetailMap({});
        setIsLoadingMonthData(true);
        const response = await attendanceService.getAttendanceHistory(
          localMonth + 1,
          localYear,
          1,
          500,
          "",
          [employee.id]
        );
        const statusMap = {};
        const detailMap = {};
        const records = Array.isArray(response?.data) ? response.data : [];
        records.forEach((record) => {
          const parts = parseYearMonthDay(record?.date);
          if (!parts) return;
          if (parts.year !== localYear || parts.month !== localMonth) return;
          const day = String(parts.day || getDayOfMonthSafe(record?.date) || "");
          if (!day) return;
          statusMap[day] = record?.status || "-";
          detailMap[day] = {
            status: record?.status || "-",
            checkIn:
              record?.checkIn ||
              record?.checkInTime ||
              record?.inTime ||
              record?.firstIn ||
              record?.firstPunchIn ||
              "--",
            checkOut:
              record?.checkOut ||
              record?.checkOutTime ||
              record?.outTime ||
              record?.lastOut ||
              record?.lastPunchOut ||
              "--",
            totalHours:
              record?.totalHours ||
              record?.workedHours ||
              record?.workingHours ||
              record?.totalWorkedHours ||
              "--",
            shiftAssigned:
              record?.shiftAssigned ||
              record?.shiftName ||
              record?.shift ||
              record?.assignedShift ||
              "--",
            isStatusOverride: !!(
              record?.isStatusOverride ?? record?.is_status_override
            ),
            statusOverriddenBy:
              record?.statusOverriddenBy ?? record?.status_overridden_by ?? null,
            statusOverriddenAt:
              record?.statusOverriddenAt ?? record?.status_overridden_at ?? null,
            statusOverriddenByName: record?.statusOverriddenByName || null,
            sandwichApplied: sandwichAppliedFromRecord(record),
            punches: Array.isArray(record?.punches) ? record.punches : [],
          };
        });
        setLocalAttendanceMap(statusMap);
        setLocalAttendanceDetailMap(detailMap);
        // Hydrate approved Change WO from server so calendar WO paint matches DB/Process.
        try {
          const { hydrateCompOffOtDeskFromServer } = await import(
            "../../../../lib/compOffOtNotify"
          );
          await hydrateCompOffOtDeskFromServer({ employeeId: employee.id });
          setPolicyRefreshTick((n) => n + 1);
        } catch {
          /* non-blocking */
        }
        setLocalMonthSynced(true);
      } catch (err) {
        console.error("Failed to load employee attendance calendar month data:", err);
        setLocalAttendanceMap({});
        setLocalAttendanceDetailMap({});
        setLocalMonthSynced(true);
      } finally {
        setIsLoadingMonthData(false);
      }
    };
    fetchEmployeeMonthAttendance();
  }, [isOpen, employee?.id, localMonth, localYear]);

  const clampToCurrentMonthMax = (year, month) => {
    const now = new Date();
    const maxY = now.getFullYear();
    const maxM = now.getMonth();
    if (year > maxY || (year === maxY && month > maxM)) {
      return { y: maxY, m: maxM };
    }
    return { y: year, m: month };
  };

  const handleLocalMonthChange = (eventOrValue) => {
    if (isMonthLocked) return;
    const rawValue =
      typeof eventOrValue === "string"
        ? eventOrValue
        : eventOrValue?.target?.value || "";
    if (!rawValue) return;
    const [start] = rawValue.split("/");
    if (!start) return;
    const [yearString, monthString] = start.split("-");
    const parsedYear = parseInt(yearString, 10);
    const parsedMonth = parseInt(monthString, 10) - 1;
    if (Number.isNaN(parsedYear) || Number.isNaN(parsedMonth)) return;
    let clamped = clampToCurrentMonthMax(
      parsedYear,
      Math.min(Math.max(parsedMonth, 0), 11)
    );
    if (employeeMinMonthYm) {
      const curYm = `${clamped.y}-${String(clamped.m + 1).padStart(2, "0")}`;
      if (curYm < employeeMinMonthYm) {
        const [my, mm] = employeeMinMonthYm.split("-").map(Number);
        if (Number.isFinite(my) && Number.isFinite(mm)) {
          clamped = { y: my, m: mm - 1 };
        }
      }
    }
    setLocalYear(clamped.y);
    setLocalMonth(clamped.m);
    setSelectedDay(getDefaultSelectedDay(clamped.y, clamped.m));
  };

  const canGoNextMonth = useMemo(() => {
    if (isMonthLocked) return false;
    const n = new Date();
    return (
      localYear < n.getFullYear() || (localYear === n.getFullYear() && localMonth < n.getMonth())
    );
  }, [localYear, localMonth, isMonthLocked]);

  const employeeMinDate = useMemo(
    () => resolveEmployeeMinDate(employee),
    [
      employee?.id,
      employee?.joiningDate,
      employee?.joinDate,
      employee?.dateOfJoin,
      employee?.dateOfJoining,
    ]
  );
  const employeeMinMonthYm = useMemo(
    () => resolveEmployeeMinMonthValue(employee),
    [
      employee?.id,
      employee?.joiningDate,
      employee?.joinDate,
      employee?.dateOfJoin,
      employee?.dateOfJoining,
    ]
  );

  const canGoPrevMonth = (() => {
    if (isMonthLocked) return false;
    if (employeeMinMonthYm) {
      const curYm = `${localYear}-${String(localMonth + 1).padStart(2, "0")}`;
      return curYm > employeeMinMonthYm;
    }
    return localYear > 1900 || (localYear === 1900 && localMonth > 0);
  })();

  const handlePrevMonth = () => {
    if (isMonthLocked || !canGoPrevMonth) return;
    if (localMonth === 0) {
      setLocalYear((y) => y - 1);
      setLocalMonth(11);
    } else {
      setLocalMonth((m) => m - 1);
    }
    setSelectedDay(getDefaultSelectedDay(localMonth === 0 ? localYear - 1 : localYear, localMonth === 0 ? 11 : localMonth - 1));
  };

  const handleNextMonth = () => {
    if (isMonthLocked || !canGoNextMonth) return;
    let y = localYear;
    let mo = localMonth;
    if (mo === 11) {
      y += 1;
      mo = 0;
    } else {
      mo += 1;
    }
    const c = clampToCurrentMonthMax(y, mo);
    setLocalYear(c.y);
    setLocalMonth(c.m);
    setSelectedDay(getDefaultSelectedDay(c.y, c.m));
  };

  const now = new Date();
  const currentMonthActiveDay =
    localYear === now.getFullYear() && localMonth === now.getMonth()
      ? now.getDate()
      : null;
  const activeDay = selectedDay != null ? selectedDay : currentMonthActiveDay;

  const selectedDayStatus =
    activeDay != null
      ? displayStatusMap?.[String(activeDay)] ?? displayStatusMap?.[activeDay] ?? "-"
      : "-";
  const selectedDateLabel =
    activeDay != null
      ? `${String(activeDay).padStart(2, "0")}-${String(localMonth + 1).padStart(2, "0")}-${localYear}`
      : "-";
  const selectedDateIso =
    activeDay != null
      ? `${localYear}-${String(localMonth + 1).padStart(2, "0")}-${String(activeDay).padStart(2, "0")}`
      : null;

  const [dayCompOff, setDayCompOff] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setDayCompOff(null);
    if (!employee?.id || !selectedDateIso) return undefined;
    (async () => {
      try {
        const res = await attendanceService.listCompOffLedger({
          employeeId: employee.id,
        });
        const rows = Array.isArray(res?.data) ? res.data : [];
        const match = rows.find(
          (r) => String(r.sourceDate || r.source_date || "").slice(0, 10) === selectedDateIso
        );
        if (!cancelled) setDayCompOff(match || null);
      } catch {
        if (!cancelled) setDayCompOff(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [employee?.id, selectedDateIso]);

  const employeeStatusEditAccess = useMemo(() => {
    if (!employee?.id) {
      return { allowed: false, requiresWorkflow: false, reason: "no_employee" };
    }
    const asOf = selectedDateIso ? new Date(selectedDateIso) : new Date(localYear, localMonth, 1);
    return resolveEmployeeStatusEditAccess(employee, asOf);
  }, [employee, selectedDateIso, localYear, localMonth, policyRefreshTick]);

  // ESS Attendance Logs (embedded): Admin/Super Admin follow the same policy gate as employees.
  // Admin Process / Change Status popup (non-embedded) may still edit without that ESS policy flag.
  const essPolicyOnlyStatusEdit = !!embedded;

  const statusEditRequiresWorkflow = essPolicyOnlyStatusEdit
    ? !!employeeStatusEditAccess.requiresWorkflow
    : !isAdmin && !!employeeStatusEditAccess.requiresWorkflow;

  const canEditStatus =
    !isSummaryMode &&
    !viewedMonthLocked &&
    typeof onEditAttendanceStatus === "function" &&
    (essPolicyOnlyStatusEdit
      ? employeeStatusEditAccess.allowed
      : isAdmin || employeeStatusEditAccess.allowed);

  const statusEditActionLabel = statusEditRequiresWorkflow
    ? "Apply for status change"
    : "Edit attendance status";

  const selectedDayActionsTip = useMemo(() => {
    const how = phoneEss ? "Choose an action below to" : "Use the ⋮ menu to";
    if (isManualMode) {
      return canEditStatus
        ? `Tip: ${how} edit status or apply leave. Weekly off and holiday policies still apply.`
        : `Tip: ${how} apply leave. Weekly off and holiday policies still apply.`;
    }
    const parts = [];
    if (canEditStatus) {
      parts.push(statusEditRequiresWorkflow ? "apply for status change" : "edit status");
    }
    if (canApplyLeave) parts.push("apply leave");
    if (canLeaveBalance) parts.push("leave balance");
    if (canCompOffLedger) parts.push("comp off ledger");
    if (canChangeShift) parts.push("change shift");
    if (canChangeWo) parts.push("change WO");
    if (canRegularise) parts.push("regularise checks");
    if (canShortLeave) parts.push("short leave");
    if (canOd) parts.push("on duty");
    if (canCompOff) parts.push("comp off");
    if (canOt) parts.push("overtime");
    if (parts.length === 0) {
      return "Day status is driven by your attendance policy and check-in/out. Contact HR for corrections.";
    }
    return `Tip: ${how} ${parts.join(", or ")} for the selected day.`;
  }, [
    phoneEss,
    isManualMode,
    canEditStatus,
    statusEditRequiresWorkflow,
    canApplyLeave,
    canLeaveBalance,
    canCompOffLedger,
    canChangeShift,
    canChangeWo,
    canRegularise,
    canShortLeave,
    canOd,
    canCompOff,
    canOt,
  ]);

  const monthStatusSummaryNote =
    !isSummaryMode && showProcessSummary && processMonthStatusSummary
      ? showFinalisedMark
        ? "Finalised attendance — status counts and payable days for this month."
        : "Counts update as you change day statuses. Review before Process."
      : "";

  const navbarDayWorklog = useMemo(() => {
    if (!selectedDateIso || !employee?.id) return null;
    const row = readEmployeeWorklog(employee.id, selectedDateIso);
    if (!row) return null;
    return {
      inMs: row.inMs,
      outMs: row.outMs,
      sessions: row.sessions,
      open: Boolean(row.open),
    };
  }, [selectedDateIso, employee?.id, policyRefreshTick]);

  const selectedDayDetails =
    activeDay != null
      ? empDetailMap?.[String(activeDay)] || empDetailMap?.[activeDay] || {}
      : {};
  const selectedDayDetailsWithNavbarLog = useMemo(() => {
    const requestShiftName = resolveApprovedChangeShiftName(employee?.id, selectedDateIso);
    const currentShift =
      requestShiftName ||
      selectedDayDetails?.shiftAssigned ||
      selectedDayDetails?.shiftName ||
      selectedDayDetails?.assignedShift;
    const needsDefaultShift =
      !currentShift ||
      String(currentShift).trim() === "" ||
      String(currentShift).trim() === "--";
    const defaultShiftName = needsDefaultShift ? resolveDefaultShiftName() : null;

    const base = {
      ...selectedDayDetails,
      ...(currentShift && String(currentShift).trim() !== "--"
        ? { shiftAssigned: currentShift }
        : defaultShiftName
        ? { shiftAssigned: defaultShiftName }
        : {}),
    };

    const rawIn =
      navbarDayWorklog?.inMs != null ? navbarDayWorklog.inMs : base.checkIn;
    const rawOut =
      navbarDayWorklog?.outMs != null ? navbarDayWorklog.outMs : base.checkOut;

    const checkIn =
      formatPunchDateTimeDisplay(rawIn, selectedDateIso) ||
      (base.checkIn && base.checkIn !== "--" ? String(base.checkIn) : "--");
    const checkOut =
      formatPunchDateTimeDisplay(rawOut, selectedDateIso) ||
      (base.checkOut && base.checkOut !== "--" ? String(base.checkOut) : "--");

    let totalHours = "--";
    const parentBucket =
      attendanceDetailData?.[employee?.id] ||
      attendanceDetailData?.[String(employee?.id)] ||
      null;
    const parentDay =
      activeDay != null && parentBucket
        ? parentBucket[String(activeDay)] || parentBucket[activeDay]
        : null;
    const punchRows =
      Array.isArray(base.punches) && base.punches.length
        ? base.punches
        : Array.isArray(parentDay?.punches)
          ? parentDay.punches
          : [];
    const punchRowMs = workedMsFromPunchRows(punchRows, Date.now());
    if (punchRowMs != null && punchRowMs > 0) {
      totalHours = formatDurationHms(punchRowMs);
    } else if (navbarDayWorklog) {
      const openSession =
        navbarDayWorklog.open === true &&
        navbarDayWorklog.outMs == null &&
        !hasLocalCheckoutGuard(employee.id, selectedDateIso);
      const nowMs = Date.now();
      const punchMs = workedMsFromSessions(
        { ...navbarDayWorklog, open: openSession },
        openSession ? nowMs : navbarDayWorklog.outMs ?? nowMs
      );
      totalHours =
        punchMs > 0
          ? formatDurationHms(punchMs)
          : resolveDayDetailHours(rawIn, rawOut, base.totalHours);
    } else {
      totalHours = resolveDayDetailHours(base.checkIn, base.checkOut, base.totalHours);
    }

    return {
      ...base,
      checkIn,
      checkOut,
      totalHours,
    };
  }, [navbarDayWorklog, selectedDayDetails, employee, selectedDateIso, hoursTick, attendanceDetailData, activeDay]);

  const showCompOffDayDetails = useMemo(() => {
    const st = String(selectedDayStatus || "")
      .toUpperCase()
      .split("/")[0];
    const hasPunch =
      (selectedDayDetailsWithNavbarLog?.checkIn &&
        selectedDayDetailsWithNavbarLog.checkIn !== "--") ||
      (selectedDayDetailsWithNavbarLog?.checkOut &&
        selectedDayDetailsWithNavbarLog.checkOut !== "--");
    return (
      !!dayCompOff ||
      ((st === "WO" || st === "CW" || st === "PH" || st === "HO") && hasPunch)
    );
  }, [dayCompOff, selectedDayStatus, selectedDayDetailsWithNavbarLog]);

  const openStatusEditModal = (dayOverride = null, statusOverride = null, halfDayMode = true) => {
    const candidateDay = dayOverride ?? selectedDay;
    const normalizedDay = Number(candidateDay);
    if (!Number.isFinite(normalizedDay) || normalizedDay < 1) return;
    const targetDay = Math.trunc(normalizedDay);
    const fallbackStatus =
      displayStatusMap?.[String(targetDay)] ?? displayStatusMap?.[targetDay] ?? "-";
    const current = String(statusOverride ?? fallbackStatus).trim();
    const parts = current.includes("/") ? current.split("/") : [current, current];
    const first = toStatusOptionCode(parts[0]) || "PR";
    const second = toStatusOptionCode(parts[1] || parts[0]) || first;
    setFirstHalfStatus(first);
    setSecondHalfStatus(second);
    setSelectedDay(targetDay);
    setStatusEditHalfDayMode(!!halfDayMode);
    setShowStatusEditModal(true);
    setShowDayActionMenu(false);
  };

  const openRegularizationModal = () => {
    if (selectedDay == null || selectedDay < 1) return;
    const dateIso = selectedDateIso ? clampDateIsoToToday(selectedDateIso) : null;
    if (dateIso && isFutureDateIso(dateIso)) return;
    setShowRegularizationModal(true);
    setShowDayActionMenu(false);
  };

  const handleRegulariseChecksSubmit = (formData) => {
    if (!employee?.id || !formData?.date) return;
    const year = formData.year ?? parseInt(formData.date.split("-")[0], 10);
    const month =
      formData.month ?? parseInt(formData.date.split("-")[1], 10) - 1;
    const day = formData.day ?? parseInt(formData.date.split("-")[2], 10);
    if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return;

    const checkIn = formData.inTime?.trim() || "--";
    const checkOut = formData.outTime?.trim() || "--";
    const totalHours = formData.totalHours?.trim() || "--";
    const dayKey = String(day);
    const sameCalendarMonth = year === localYear && month === localMonth;

    if (sameCalendarMonth && typeof onSaveRegularizationTimes === "function") {
      onSaveRegularizationTimes(employee, {
        day,
        year,
        month,
        checkIn,
        checkOut,
        totalHours,
      });
    }
    if (!sameCalendarMonth) {
      setLocalAttendanceDetailMap((prev) => ({
        ...prev,
        [dayKey]: {
          ...(prev?.[dayKey] || {}),
          checkIn,
          checkOut,
          totalHours,
        },
      }));
    }

    reloadRegularisationSessionLog();

    const currentUser = getAuthJson("currentUser", null);
    const mode = embedded ? "ess" : "admin_direct";
    submitRegulariseRequest({
      employeeId: String(employee.id),
      userId: currentUser?.id || null,
      userName: employee?.name || employee?.user?.name || currentUser?.name || "Employee",
      userEmail: employee?.email || "",
      formData,
      mode,
      raisedByName:
        mode === "admin_direct"
          ? currentUser?.name || "Admin"
          : currentUser?.name || "Employee",
    }).catch(() => {
      /* ignore storage failures */
    });

    setShowRegularizationModal(false);
  };

  const reloadAttendanceHistoryForEmployee = async (empId, yr, mo) => {
    try {
      const response = await attendanceService.getAttendanceHistory(
        mo + 1,
        yr,
        1,
        500,
        "",
        [empId]
      );
      const statusMap = {};
      const detailMap = {};
      const records = Array.isArray(response?.data) ? response.data : [];
      records.forEach((record) => {
        const parts = parseYearMonthDay(record?.date);
        if (!parts) return;
        if (parts.year !== yr || parts.month !== mo) return;
        const day = String(parts.day || getDayOfMonthSafe(record?.date) || "");
        if (!day) return;
        statusMap[day] = record?.status || "-";
        detailMap[day] = {
          status: record?.status || "-",
          checkIn:
            record?.checkIn ||
            record?.checkInTime ||
            record?.inTime ||
            record?.firstIn ||
            record?.firstPunchIn ||
            "--",
          checkOut:
            record?.checkOut ||
            record?.checkOutTime ||
            record?.outTime ||
            record?.lastOut ||
            record?.lastPunchOut ||
            "--",
          totalHours:
            record?.totalHours ||
            record?.workedHours ||
            record?.workingHours ||
            record?.totalWorkedHours ||
            "--",
          shiftAssigned:
            record?.shiftAssigned ||
            record?.shiftName ||
            record?.shift ||
            record?.assignedShift ||
            "--",
          isStatusOverride: !!(
            record?.isStatusOverride ?? record?.is_status_override
          ),
          statusOverriddenBy:
            record?.statusOverriddenBy ?? record?.status_overridden_by ?? null,
          statusOverriddenAt:
            record?.statusOverriddenAt ?? record?.status_overridden_at ?? null,
          statusOverriddenByName: record?.statusOverriddenByName || null,
        };
      });
      setLocalAttendanceMap(statusMap);
      setLocalAttendanceDetailMap(detailMap);
      setLocalMonthSynced(true);
    } catch (reloadErr) {
      console.warn("Calendar reload after save skipped:", reloadErr);
    }
  };

  const handleSaveEditedStatus = async () => {
    if (selectedDay == null || selectedDay < 1) return;
    if (viewedMonthLocked) {
      toast.error("This month is locked. Unlock or Unprocess attendance before changing the calendar.");
      return;
    }
    const combinedStatus = combineStatuses(firstHalfStatus, secondHalfStatus);
    const dayKey = String(selectedDay);

    // Locked D3: never Change Status on calendar WO/PH
    if (typeof getDayDefault === "function" && employee) {
      const dateObj = new Date(localYear, localMonth, selectedDay);
      const calDef = String(getDayDefault(dateObj, employee) || "")
        .toUpperCase()
        .split("/")[0];
      if (calDef === "WO" || calDef === "PH" || calDef === "HO") {
        toast.error(
          "Cannot Change Status on Weekly Off / Public Holiday. Use Change WO where allowed."
        );
        return;
      }
    }
    const newHead = String(combinedStatus || "")
      .toUpperCase()
      .split("/")[0];
    if (newHead === "WO" || newHead === "PH" || newHead === "HO") {
      toast.error(
        "Cannot set Weekly Off / Public Holiday via Change Status on a working day."
      );
      return;
    }

    if (statusEditRequiresWorkflow) {
      const dateIso =
        selectedDateIso ||
        `${localYear}-${String(localMonth + 1).padStart(2, "0")}-${String(selectedDay).padStart(2, "0")}`;
      const requestEntry = {
        requestId: `req_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        panelKey: "review",
        actionLabel: "Status change request",
        employeeId: String(employee.id),
        employeeName:
          employee?.name || employee?.user?.name || employee?.employeeName || "Employee",
        date: dateIso,
        year: localYear,
        month: localMonth,
        day: selectedDay,
        requestedStatus: combinedStatus,
        status: "pending",
        createdAt: Date.now(),
        source: "ess_status_edit",
      };
      try {
        const raw = localStorage.getItem("adminQuickActionRequests");
        const existing = raw ? JSON.parse(raw) : [];
        const list = Array.isArray(existing) ? existing : [];
        localStorage.setItem(
          "adminQuickActionRequests",
          JSON.stringify([requestEntry, ...list])
        );
      } catch {
        /* ignore */
      }
      toast.success("Status change submitted for approval");
      setShowStatusEditModal(false);
      return;
    }

    if (typeof onEditAttendanceStatus === "function") {
      onEditAttendanceStatus(employee, selectedDay, combinedStatus, localYear, localMonth, {
        manualStatusOverride: true,
      });
    }

    const nextAttendanceMap = {
      ...(localAttendanceMap || {}),
      [dayKey]: combinedStatus,
    };
    const nextDetailMap = {
      ...(localAttendanceDetailMap || {}),
      [dayKey]: {
        ...(localAttendanceDetailMap?.[dayKey] || {}),
        ...(empDetailMap?.[dayKey] || {}),
        status: combinedStatus,
        isStatusOverride: true,
      },
    };
    setLocalAttendanceMap(nextAttendanceMap);
    setLocalAttendanceDetailMap(nextDetailMap);
    if (isLocalCurrentMonth) {
      setLocalMonthSynced(true);
    }

    // Unify functionality of outside save: auto-persist to database immediately
    if (typeof onPersistMonth === "function") {
      setIsSavingStatus(true);
      try {
        const result = await onPersistMonth(employee, {
          year: localYear,
          month: localMonth,
          statusMap: isLocalCurrentMonth ? undefined : nextAttendanceMap,
        });
        if (result === false) {
          setIsSavingStatus(false);
          return;
        }
        setLocalMonthSynced(false);
        await reloadAttendanceHistoryForEmployee(employee.id, localYear, localMonth);
        setShowStatusEditModal(false);
        toast.success("Attendance status updated and saved successfully.");
      } catch (err) {
        console.error("Failed to save attendance status:", err);
        toast.error("Failed to save attendance status.");
      } finally {
        setIsSavingStatus(false);
      }
    } else {
      setShowStatusEditModal(false);
      toast.success("Status override staged.");
    }
  };

  const handleClearStatusOverride = async () => {
    if (selectedDay == null || selectedDay < 1 || !employee?.id) return;
    if (viewedMonthLocked) {
      toast.error("This month is locked. Unlock or Unprocess attendance before changing the calendar.");
      return;
    }
    const dayKey = String(selectedDay);
    const currentStatus =
      displayStatusMap?.[dayKey] ??
      displayStatusMap?.[selectedDay] ??
      empStatusMap?.[dayKey] ??
      "-";
    if (typeof onEditAttendanceStatus === "function") {
      onEditAttendanceStatus(employee, selectedDay, currentStatus, localYear, localMonth, {
        clearStatusOverride: true,
      });
    }
    setLocalAttendanceDetailMap((prev) => ({
      ...prev,
      [dayKey]: {
        ...(prev?.[dayKey] || {}),
        ...(empDetailMap?.[dayKey] || {}),
        isStatusOverride: false,
        statusOverriddenBy: null,
        statusOverriddenAt: null,
        statusOverriddenByName: null,
      },
    }));
    setLocalMonthSynced(true);
    if (typeof onPersistMonth === "function") {
      try {
        const result = await onPersistMonth(employee, {
          year: localYear,
          month: localMonth,
        });
        if (result !== false) {
          setLocalMonthSynced(false);
          await reloadAttendanceHistoryForEmployee(employee.id, localYear, localMonth);
          toast.success("Override cleared and saved successfully.");
          return;
        }
      } catch (err) {
        console.error("Calendar clear override save failed:", err);
        toast.error("Failed to clear status override.");
        return;
      }
    }
    toast.success("Override clear staged.");
  };

  const handleCancelForm = () => {
    onClose?.();
  };

  const handleSaveForm = async () => {
    if (viewedMonthLocked) {
      toast.error("This month is locked. Unlock or Unprocess attendance before changing the calendar.");
      return;
    }
    if (typeof onPersistMonth === "function") {
      try {
        const result = await onPersistMonth(employee, {
          year: localYear,
          month: localMonth,
          statusMap: isLocalCurrentMonth ? undefined : localAttendanceMap,
        });
        if (result === false) return;
        setLocalMonthSynced(false);
        await reloadAttendanceHistoryForEmployee(employee.id, localYear, localMonth);
      } catch (err) {
        console.error("Calendar save failed:", err);
        return;
      }
    }
    onClose?.();
  };

  if ((!isOpen && !embedded) || !employee) return null;

  const queue = Array.isArray(employeeQueue) ? employeeQueue.filter(Boolean) : [];
  const queueActive = queue.length > 0;
  const queueIdx = Math.min(
    Math.max(0, Number(employeeQueueIndex) || 0),
    Math.max(0, queue.length - 1)
  );
  const canPrevEmployee = queue.length > 1 && queueIdx > 0;
  const canNextEmployee = queue.length > 1 && queueIdx < queue.length - 1;
  const empLabel =
    employee?.empCode ||
    employee?.employeeCode ||
    employee?.code ||
    (employee?.employeeId != null &&
    String(employee.employeeId) !== String(employee?.id)
      ? employee.employeeId
      : "") ||
    "";
  const empDisplayName =
    [employee?.user?.name, employee?.user?.lastName].filter(Boolean).join(" ").trim() ||
    employee?.employeeName ||
    employee?.name ||
    employee?.fullName ||
    [employee?.firstName, employee?.lastName].filter(Boolean).join(" ") ||
    "Employee";

  const renderDayPanel = (panel) =>
    phoneEss ? (
      <MobileSheet
        open={phoneDaySheet && selectedDay != null}
        onClose={() => setPhoneDaySheet(false)}
        ariaLabel="Day details"
      >
        {panel}
      </MobileSheet>
    ) : (
      panel
    );

  return (
    <div
      className={
        embedded
          ? "h-full min-h-0"
          : "fixed inset-0 bg-black/50 z-[10030] flex items-center justify-center p-4"
      }
    >
      <div
        className={`relative ${
          embedded ? "z-[1] w-full h-full" : "z-[10031] w-[min(85.5vw,973px)] h-[min(74.1vh,684px)]"
        } rounded-xl ${phoneEss ? "shadow-sm" : "shadow-xl"} border flex flex-col overflow-visible ${
          darkMode
            ? "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))]"
            : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))]"
        }`}
      >
        <div
          className={`shrink-0 px-[calc(1.25rem*0.95)] py-[calc(0.75rem*0.95)] border-b flex items-start justify-between gap-[calc(0.75rem*0.95)] ${
            darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"
          }`}
        >
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-base font-semibold">Calendar</h3>
              {showFinalisedMark && (
                <DsBadge tone="success">Finalised</DsBadge>
              )}
              {queueActive && (
                <div className="inline-flex items-center gap-1">
                  <button
                    type="button"
                    disabled={!canPrevEmployee}
                    onClick={() => onEmployeeQueueNavigate?.(queueIdx - 1)}
                    className={`rounded border p-1.5 disabled:opacity-40 ${
                      darkMode
                        ? "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                        : "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                    }`}
                    aria-label="Previous employee"
                    title="Previous selected employee"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span
                    className={`min-w-[4.5rem] text-center text-[11px] font-semibold tabular-nums ${
                      darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
                    }`}
                  >
                    {queueIdx + 1} of {queue.length}
                  </span>
                  <button
                    type="button"
                    disabled={!canNextEmployee}
                    onClick={() => onEmployeeQueueNavigate?.(queueIdx + 1)}
                    className={`rounded border p-1.5 disabled:opacity-40 ${
                      darkMode
                        ? "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                        : "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                    }`}
                    aria-label="Next employee"
                    title="Next selected employee"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
            <p
              className={`mt-1 truncate text-xs font-medium ${
                darkMode ? "text-[hsl(var(--ds-fg))]" : "text-[hsl(var(--ds-fg))]"
              }`}
              title={`${empLabel} — ${empDisplayName}`}
            >
              {empLabel ? `${empLabel} — ${empDisplayName}` : empDisplayName}
            </p>
            {demoPolicyReady && loggedInEmployeeId && String(employee?.id) === loggedInEmployeeId && (
              <p
                className={`mt-1 text-[10px] leading-snug max-w-md ${
                  darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
                }`}
                title={`${demoTestGuide.examplePresent}. ${demoTestGuide.exampleLop}.`}
              >
                Auto attendance: {demoTestGuide.policyName} · {demoTestGuide.todayBehavior}
              </p>
            )}
          </div>
          {!embedded && (
            <button
              type="button"
              onClick={onClose}
              className={`p-1.5 rounded ${
                darkMode ? "hover:bg-[hsl(var(--ds-muted))]" : "hover:bg-[hsl(var(--ds-muted))]"
              }`}
              aria-label="Close attendance calendar"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="shrink-0 px-[calc(1.25rem*0.95)] py-[calc(0.75rem*0.95)] border-b border-[hsl(var(--ds-border))] dark:border-[hsl(var(--ds-border))] relative z-20 overflow-visible">
          <div className="flex items-center justify-between gap-[calc(0.75rem*0.95)] flex-wrap">
            <div className={`text-sm font-medium ${phoneEss ? "sr-only" : ""}`}>
              Month
              {isMonthLocked ? (
                <span className={`ml-2 text-[10px] font-normal ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
                  (process month — locked)
                </span>
              ) : null}
            </div>
            <div className={`flex items-center gap-[calc(0.5rem*0.95)] justify-end min-w-0 ${phoneEss ? "w-full" : ""}`}>
              <button
                type="button"
                onClick={handlePrevMonth}
                disabled={!canGoPrevMonth}
                className={`shrink-0 p-2 rounded border ${phoneEss ? "inline-flex h-11 w-11 items-center justify-center !rounded-xl" : ""} ${
                  !canGoPrevMonth
                    ? "opacity-40 cursor-not-allowed"
                    : darkMode
                    ? "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                    : "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                }`}
                aria-label="Previous month"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className={`relative z-20 ${phoneEss ? "min-w-0 flex-1" : "flex-none"}`}>
                <DatePicker
                  monthYearOnly
                  showMonthName
                  value={getMonthRangeValue(localYear, localMonth)}
                  onChange={isMonthLocked ? undefined : handleLocalMonthChange}
                  min={employeeMinDate || undefined}
                  max={new Date()}
                  darkMode={darkMode}
                  disabled={isMonthLocked}
                  dropdownAlign="auto"
                  dropdownClassName="!z-[10050] shadow-2xl"
                  className={`z-10 ${phoneEss ? "!w-full" : "!w-auto"} min-w-[142.5px] ${
                    isMonthLocked ? "pointer-events-none opacity-80" : ""
                  }`}
                />
              </div>
              <button
                type="button"
                onClick={handleNextMonth}
                disabled={!canGoNextMonth}
                className={`shrink-0 p-2 rounded border ${phoneEss ? "inline-flex h-11 w-11 items-center justify-center !rounded-xl" : ""} ${
                  !canGoNextMonth
                    ? "opacity-40 cursor-not-allowed"
                    : darkMode
                    ? "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                    : "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                }`}
                aria-label="Next month"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto grid grid-cols-1 lg:grid-cols-3 gap-0">
          <div className={`lg:col-span-2 border-r border-[hsl(var(--ds-border))] dark:border-[hsl(var(--ds-border))] p-[calc(1rem*0.95)] relative ${phoneEss ? "!border-r-0 !p-3" : ""}`}>
            {showCalendarLoading && (
              <div
                className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-[hsl(var(--ds-bg)/0.85)] backdrop-blur-[1px]"
                aria-busy="true"
                aria-live="polite"
              >
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-[hsl(var(--ds-border))] border-t-[hsl(var(--ds-primary))]" />
                <p className="text-xs text-[hsl(var(--ds-muted-fg))]">Loading attendance…</p>
              </div>
            )}
            <div className={`grid grid-cols-7 mb-[calc(0.5rem*0.95)] ${phoneEss ? "gap-1" : "gap-[calc(0.5rem*0.95)]"}`}>
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((name) => (
                <div
                  key={name}
                  className={`${phoneEss ? "text-[11px]" : "text-[10.5px]"} font-semibold text-center ${
                    darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-fg))]"
                  }`}
                >
                  {name}
                </div>
              ))}
            </div>

            <div
              className={`grid grid-cols-7 ${phoneEss ? "gap-1" : "gap-[calc(0.5rem*0.95)]"} ${
                showCalendarLoading ? "opacity-30 pointer-events-none" : ""
              }`}
              onClick={() => {
                setSelectedDay(null);
              }}
            >
              {Array.from({ length: dayOffset }).map((_, idx) => (
                <StatusDayCell
                  key={`blank-${idx}`}
                  day=""
                  mute
                  onClick={() => setSelectedDay(null)}
                />
              ))}

              {Array.from({ length: daysInMonth }, (_, idx) => {
                const day = idx + 1;
                const status =
                  displayStatusMap?.[String(day)] ?? displayStatusMap?.[day] ?? "-";
                const isToday =
                  localYear === now.getFullYear() &&
                  localMonth === now.getMonth() &&
                  day === now.getDate();
                const sandwichUnpaid = sandwichAppliedFromRecord(
                  localAttendanceDetailMap?.[String(day)] ||
                    localAttendanceDetailMap?.[day]
                );
                const kind = statusToMarkKind(status);
                return (
                  <StatusDayCell
                    key={day}
                    as="button"
                    day={day}
                    kind={kind}
                    isToday={isToday}
                    selected={selectedDay === day}
                    title={
                      sandwichUnpaid
                        ? "Sandwich — weekly off converted to LP"
                        : undefined
                    }
                    className={
                      phoneEss
                        ? "active:scale-95 motion-reduce:active:scale-100"
                        : ""
                    }
                    onClick={(event) => {
                      event.stopPropagation();
                      setSelectedDay(day);
                      if (phoneEss) {
                        setPhoneDaySheet(true);
                        return;
                      }
                      if (!canEditStatus) return;
                      if (dayClickTimerRef.current) clearTimeout(dayClickTimerRef.current);
                      // Single click → full-day status (both halves same). Double-click cancels this.
                      dayClickTimerRef.current = setTimeout(() => {
                        openStatusEditModal(day, null, false);
                      }, 280);
                    }}
                    onDoubleClick={(event) => {
                      event.stopPropagation();
                      event.preventDefault();
                      if (phoneEss) return;
                      if (dayClickTimerRef.current) {
                        clearTimeout(dayClickTimerRef.current);
                        dayClickTimerRef.current = null;
                      }
                      setSelectedDay(day);
                      if (canEditStatus) {
                        openStatusEditModal(day, null, true);
                      }
                    }}
                  />
                );
              })}
            </div>
          </div>

          <div className={`p-[calc(1rem*0.95)] flex flex-col h-full min-h-0 ${phoneEss ? "!px-3 !pb-3 !pt-0 empty:hidden" : ""}`}>
            {renderDayPanel(
            <div className="space-y-[calc(0.75rem*0.95)]">
            <h4 className="text-sm font-semibold">Day details</h4>
            <div
              className={`rounded-lg border p-[calc(0.75rem*0.95)] ${phoneEss ? "text-sm" : "text-xs"} space-y-1 ${
                darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]"
              }`}
            >
              <div>
                <span className="font-medium">Date:</span> {selectedDateLabel}
              </div>
              <div className="flex flex-wrap items-baseline gap-x-1 gap-y-0">
                <span className="font-medium">Status:</span>
                {renderColoredStatusContent(
                  selectedDayStatus,
                  attendanceMapping,
                  codeColorMap,
                  darkMode,
                  phoneEss ? "text-sm" : "text-xs"
                )}
              </div>
              {sandwichAppliedFromRecord(selectedDayDetailsWithNavbarLog) && (
                <div className="text-[hsl(var(--ds-warning))]">
                  Sandwich — weekly off converted to LP
                </div>
              )}
              <div>
                <span className="font-medium">Check-in:</span> {selectedDayDetailsWithNavbarLog.checkIn || "--"}
              </div>
              <div>
                <span className="font-medium">Check-out:</span> {selectedDayDetailsWithNavbarLog.checkOut || "--"}
              </div>
              <div>
                <span className="font-medium">Total hours:</span> {selectedDayDetailsWithNavbarLog.totalHours || "--"}
              </div>
              <div>
                <span className="font-medium">Shift assigned:</span>{" "}
                {selectedDayDetailsWithNavbarLog.shiftAssigned || "--"}
              </div>
              {(selectedDayDetailsWithNavbarLog.isStatusOverride ||
                selectedDayDetailsWithNavbarLog.is_status_override) && (
                <div
                  className={`mt-2 space-y-1 border-t pt-2 ${
                    darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"
                  }`}
                >
                  <div className="font-medium text-[hsl(var(--ds-fg))]">Status override</div>
                  <div>
                    <span className="font-medium">Changed by:</span>{" "}
                    {selectedDayDetailsWithNavbarLog.statusOverriddenByName ||
                      (selectedDayDetailsWithNavbarLog.statusOverriddenBy
                        ? `User #${selectedDayDetailsWithNavbarLog.statusOverriddenBy}`
                        : "—")}
                  </div>
                  <div>
                    <span className="font-medium">Changed at:</span>{" "}
                    {selectedDayDetailsWithNavbarLog.statusOverriddenAt
                      ? (() => {
                          try {
                            const d = new Date(selectedDayDetailsWithNavbarLog.statusOverriddenAt);
                            if (Number.isNaN(d.getTime())) return "—";
                            return d.toLocaleString(undefined, {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            });
                          } catch {
                            return "—";
                          }
                        })()
                      : "—"}
                  </div>
                  {!embedded &&
                    !isSummaryMode &&
                    !viewedMonthLocked &&
                    typeof onEditAttendanceStatus === "function" && (
                    <button
                      type="button"
                      onClick={handleClearStatusOverride}
                      className="mt-1 text-[11px] font-semibold text-[hsl(var(--ds-primary))] hover:underline"
                    >
                      Clear override
                    </button>
                  )}
                </div>
              )}
              {showCompOffDayDetails && (
                <div
                  className={`mt-2 space-y-1 border-t pt-2 ${
                    darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"
                  }`}
                >
                  <div className="font-medium text-[hsl(var(--ds-fg))]">Comp Off (this day)</div>
                  <div>
                    <span className="font-medium">Hours worked:</span>{" "}
                    {dayCompOff?.hoursWorked != null
                      ? `${dayCompOff.hoursWorked}h`
                      : selectedDayDetailsWithNavbarLog.totalHours || "--"}
                  </div>
                  <div>
                    <span className="font-medium">Credit days:</span>{" "}
                    {dayCompOff?.creditDays != null ? dayCompOff.creditDays : "--"}
                  </div>
                  <div>
                    <span className="font-medium">Ledger status:</span>{" "}
                    {dayCompOff?.status || "Not raised yet"}
                  </div>
                  {dayCompOff?.sourceType && (
                    <div>
                      <span className="font-medium">Source:</span> {dayCompOff.sourceType}
                    </div>
                  )}
                </div>
              )}
            </div>

            {isSummaryMode ? (
              <div
                className={`rounded-lg border p-[calc(0.75rem*0.95)] text-xs space-y-2 ${
                  darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <h4 className="text-sm font-semibold">Month summary</h4>
                  {showFinalisedMark && (
                    <DsBadge tone="success">Finalised</DsBadge>
                  )}
                </div>
                {showProcessSummary && processMonthStatusSummary && (
                  <div className="grid grid-cols-3 gap-1.5">
                    {processMonthStatusSummary.entries.map(({ code, displayCode, count }) => {
                      const pres = getStatusColorPresentation(displayCode, codeColorMap, darkMode);
                      return (
                        <div
                          key={`sum-status-${code}`}
                          className={`rounded border px-2 py-1.5 ${
                            darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
                          }`}
                        >
                          <p
                            className={`text-[10px] uppercase font-semibold ${pres.className}`}
                            style={pres.style}
                          >
                            {displayCode}
                          </p>
                          <p className="text-sm font-semibold tabular-nums">
                            {formatStatusCount(count)}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-3">
                  <div
                    className={`rounded border px-2 py-1.5 ${
                      darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
                    }`}
                  >
                    <p className={`text-[10px] uppercase ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
                      Total days
                    </p>
                    <p className="text-sm font-semibold tabular-nums">
                      {monthSummary?.totalDays ?? daysInMonth}
                    </p>
                  </div>
                  <div
                    className={`rounded border px-2 py-1.5 ${
                      darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
                    }`}
                  >
                    <p className={`text-[10px] uppercase ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
                      Payable days
                    </p>
                    <p className="text-sm font-semibold tabular-nums">
                      {showProcessSummary && processMonthStatusSummary
                        ? formatStatusCount(processMonthStatusSummary.payDays)
                        : (monthSummary?.payDays ?? "—")}
                    </p>
                  </div>
                  <div
                    className={`rounded border px-2 py-1.5 ${
                      darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
                    }`}
                  >
                    <p className={`text-[10px] uppercase ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
                      LOP reversal
                    </p>
                    <p className="text-sm font-semibold tabular-nums">
                      {monthSummary?.lopReversal ?? 0}
                    </p>
                  </div>
                </div>
                <p className={`text-[10px] ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
                  Processed attendance view — day status edits are done from Change Status before
                  Process.
                </p>
              </div>
            ) : hideSelectedDayActions ? null : (
            <div className={`flex items-center justify-between gap-2 ${phoneEss ? "flex-wrap" : ""}`}>
              <div className="flex min-w-0 items-center gap-1">
              <h4 className="text-sm font-semibold">Actions for selected day</h4>
              <div className="relative shrink-0" ref={dayActionsInfoBtnRef}>
                <button
                  type="button"
                  onClick={() => {
                    setShowDayActionMenu(false);
                    setShowMonthSummaryInfo(false);
                    setShowDayActionsInfo((prev) => !prev);
                  }}
                  className={`rounded p-1 text-[hsl(var(--ds-muted-fg))] ${
                    darkMode
                      ? "hover:bg-[hsl(var(--ds-muted))]"
                      : "hover:bg-[hsl(var(--ds-muted))]"
                  }`}
                  aria-label="Actions for selected day details"
                  aria-expanded={showDayActionsInfo}
                  title="Info"
                >
                  <DsIcon icon={Info} size="sm" />
                </button>
                {showDayActionsInfo &&
                  typeof document !== "undefined" &&
                  createPortal(
                    <div
                      data-day-actions-info
                      role="tooltip"
                      className={`fixed ${phoneEss ? "z-[12010]" : "z-[10040]"} overflow-y-auto rounded-md border p-3 text-left text-xs leading-snug shadow-lg ${
                        darkMode
                          ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-muted-fg))]"
                          : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-muted-fg))]"
                      }`}
                      style={{
                        top: dayActionsInfoCoords.top,
                        left: dayActionsInfoCoords.left,
                        width: dayActionsInfoCoords.width || 288,
                        maxHeight: dayActionsInfoCoords.maxHeight || 220,
                      }}
                    >
                      {selectedDayActionsTip}
                    </div>,
                    document.body
                  )}
              </div>
              </div>
              <div className={phoneEss ? "basis-full" : "relative"} ref={dayActionBtnRef}>
                <button
                  type="button"
                  onClick={() => {
                    setShowDayActionsInfo(false);
                    setShowMonthSummaryInfo(false);
                    setShowDayActionMenu((prev) => !prev);
                  }}
                  className={`p-1.5 rounded border ${phoneEss ? "hidden" : ""} ${
                    darkMode
                      ? "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                      : "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                  }`}
                  title="More actions"
                >
                  <MoreVertical className="w-4 h-4" />
                </button>
                {(showDayActionMenu || phoneEss) &&
                  typeof document !== "undefined" &&
                  (phoneEss ? (node) => node : (node) => createPortal(node, document.body))(
                  <div
                    data-day-action-menu
                    onClickCapture={
                      phoneEss
                        ? (e) => e.target.closest("button:not(:disabled)") && setPhoneDaySheet(false)
                        : undefined
                    }
                    className={
                      phoneEss
                        ? "mt-1 overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] empty:hidden [&>button]:min-h-[44px] [&>button]:px-4 [&>button]:text-sm"
                        : `fixed z-[10040] w-44 max-w-[calc(100vw-1rem)] overflow-y-auto overscroll-contain rounded-md border shadow-lg ${
                      darkMode
                        ? "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"
                        : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"
                    }`}
                    style={
                      phoneEss
                        ? undefined
                        : {
                      top: dayActionMenuCoords.top,
                      left: dayActionMenuCoords.left,
                      maxHeight: dayActionMenuCoords.maxHeight || 260,
                    }}
                  >
                    {canEditStatus && (
                    <button
                      type="button"
                      disabled={viewedMonthLocked}
                      onClick={() => {
                        openStatusEditModal();
                      }}
                      className={`w-full text-left px-3 py-2.5 text-xs font-medium ${
                        viewedMonthLocked
                          ? "opacity-60 cursor-not-allowed"
                          : darkMode
                          ? "hover:bg-[hsl(var(--ds-muted))]"
                          : "hover:bg-[hsl(var(--ds-muted))]"
                      }`}
                      title={viewedMonthLocked ? "Attendance is locked for this month. Unlock or Unprocess before changing it." : statusEditActionLabel}
                    >
                      {statusEditActionLabel}
                    </button>
                    )}
                    {canApplyLeave && (
                    <button
                      type="button"
                      disabled={viewedMonthLocked}
                      onClick={() => {
                        setShowDayActionMenu(false);
                        const dateIso = selectedDateIso || undefined;
                        onApplyLeave?.(
                          employee,
                          dateIso
                            ? { fromDate: dateIso, toDate: dateIso, date: dateIso }
                            : {}
                        );
                      }}
                      className={`w-full text-left px-3 py-2.5 text-xs font-medium border-t ${
                        viewedMonthLocked
                          ? "opacity-60 cursor-not-allowed"
                          : darkMode
                          ? "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                          : "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                      }`}
                    >
                      Apply Leave
                    </button>
                    )}
                    {canLeaveBalance && (
                    <button
                      type="button"
                      onClick={() => {
                        setShowDayActionMenu(false);
                        onLeaveBalance?.(employee);
                      }}
                      className={`w-full text-left px-3 py-2.5 text-xs font-medium border-t ${
                        darkMode
                          ? "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                          : "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                      }`}
                    >
                      Leave Balance
                    </button>
                    )}
                    {canCompOffLedger && (
                    <button
                      type="button"
                      onClick={() => {
                        setShowDayActionMenu(false);
                        onCompOffLedger?.(employee);
                      }}
                      className={`w-full text-left px-3 py-2.5 text-xs font-medium border-t ${
                        darkMode
                          ? "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                          : "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                      }`}
                    >
                      Comp Off Ledger
                    </button>
                    )}
                    {canChangeShift && (
                    <button
                      type="button"
                      disabled={viewedMonthLocked}
                      onClick={() => {
                        setShowDayActionMenu(false);
                        const dateIso = selectedDateIso || undefined;
                        onChangeShift?.(employee, dateIso ? { date: dateIso } : {});
                      }}
                      className={`w-full text-left px-3 py-2.5 text-xs font-medium border-t ${
                        viewedMonthLocked
                          ? "opacity-60 cursor-not-allowed"
                          : darkMode
                          ? "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                          : "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                      }`}
                    >
                      Change Shift
                    </button>
                    )}
                    {canChangeWo && (
                    <button
                      type="button"
                      disabled={viewedMonthLocked}
                      onClick={() => {
                        setShowDayActionMenu(false);
                        const dateIso = selectedDateIso || undefined;
                        onChangeWo?.(employee, dateIso ? { date: dateIso } : {});
                      }}
                      className={`w-full text-left px-3 py-2.5 text-xs font-medium border-t ${
                        viewedMonthLocked
                          ? "opacity-60 cursor-not-allowed"
                          : darkMode
                          ? "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                          : "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                      }`}
                    >
                      Change WO
                    </button>
                    )}
                    {canRegularise && (
                    <button
                      type="button"
                      disabled={viewedMonthLocked}
                      onClick={() => {
                        openRegularizationModal();
                      }}
                      className={`w-full text-left px-3 py-2.5 text-xs font-medium border-t ${
                        viewedMonthLocked
                          ? "opacity-60 cursor-not-allowed"
                          : darkMode
                          ? "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                          : "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                      }`}
                    >
                      Regularise checks
                    </button>
                    )}
                    {canOpenViewChecks && (
                    <button
                      type="button"
                      disabled={viewedMonthLocked && !viewChecksReadOnly && !embedded}
                      onClick={() => {
                        if (viewedMonthLocked && !viewChecksReadOnly && !embedded) return;
                        setShowDayActionMenu(false);
                        if (!selectedDateIso) return;
                        onViewEditChecks?.(employee, selectedDateIso);
                      }}
                      className={`w-full text-left px-3 py-2.5 text-xs font-medium border-t ${
                        darkMode
                          ? "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                          : "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                      }`}
                    >
                      {viewChecksReadOnly || embedded ? "View checks" : "View and Edit Checks"}
                    </button>
                    )}
                    {canShortLeave && (
                    <button
                      type="button"
                      disabled={viewedMonthLocked}
                      onClick={() => {
                        setShowDayActionMenu(false);
                        const dateIso = selectedDateIso || undefined;
                        onShortLeave?.(employee, dateIso ? { date: dateIso } : {});
                      }}
                      className={`w-full text-left px-3 py-2.5 text-xs font-medium border-t ${
                        viewedMonthLocked
                          ? "opacity-60 cursor-not-allowed"
                          : darkMode
                          ? "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                          : "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                      }`}
                    >
                      Short Leave
                    </button>
                    )}
                    {canOd && (
                    <button
                      type="button"
                      disabled={viewedMonthLocked}
                      onClick={() => {
                        setShowDayActionMenu(false);
                        const dateIso = selectedDateIso || undefined;
                        onOdRequest?.(employee, dateIso ? { date: dateIso } : {});
                      }}
                      className={`w-full text-left px-3 py-2.5 text-xs font-medium border-t ${
                        viewedMonthLocked
                          ? "opacity-60 cursor-not-allowed"
                          : darkMode
                          ? "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                          : "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                      }`}
                    >
                      On Duty (OD)
                    </button>
                    )}
                    {canCompOff && (
                    <button
                      type="button"
                      disabled={viewedMonthLocked}
                      onClick={() => {
                        setShowDayActionMenu(false);
                        const dateIso = selectedDateIso || undefined;
                        onCompOffRequest?.(employee, dateIso ? { date: dateIso } : {});
                      }}
                      className={`w-full text-left px-3 py-2.5 text-xs font-medium border-t ${
                        viewedMonthLocked
                          ? "opacity-60 cursor-not-allowed"
                          : darkMode
                          ? "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                          : "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                      }`}
                    >
                      Comp Off
                    </button>
                    )}
                    {canOt && (
                    <button
                      type="button"
                      disabled={viewedMonthLocked}
                      onClick={() => {
                        setShowDayActionMenu(false);
                        const dateIso = selectedDateIso || undefined;
                        onOtRequest?.(employee, dateIso ? { date: dateIso } : {});
                      }}
                      className={`w-full text-left px-3 py-2.5 text-xs font-medium border-t ${
                        viewedMonthLocked
                          ? "opacity-60 cursor-not-allowed"
                          : darkMode
                          ? "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                          : "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                      }`}
                    >
                      Overtime (OT)
                    </button>
                    )}
                    {canRh && (
                    <button
                      type="button"
                      disabled={viewedMonthLocked}
                      onClick={() => {
                        setShowDayActionMenu(false);
                        const dateIso = selectedDateIso || undefined;
                        onRhRequest?.(employee, dateIso ? { date: dateIso } : {});
                      }}
                      className={`w-full text-left px-3 py-2.5 text-xs font-medium border-t ${
                        viewedMonthLocked
                          ? "opacity-60 cursor-not-allowed"
                          : darkMode
                          ? "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                          : "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                      }`}
                    >
                      Restricted Holiday (RH)
                    </button>
                    )}
                  </div>
                )}
              </div>
            </div>
            )}
            </div>
            )}
            {!isSummaryMode && showProcessSummary && showCalendarLoading && (
              <div
                className={`mt-auto pt-[calc(0.75rem*0.95)] rounded-lg border p-[calc(0.75rem*0.95)] text-xs ${
                  darkMode
                    ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
                    : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]"
                }`}
              >
                <p className="text-[hsl(var(--ds-muted-fg))]">Loading month summary…</p>
              </div>
            )}
            {!isSummaryMode && showProcessSummary && processMonthStatusSummary && (
              <div
                className={`mt-auto pt-[calc(0.75rem*0.95)] rounded-lg border p-[calc(0.75rem*0.95)] text-xs space-y-2 ${
                  darkMode
                    ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
                    : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-1">
                    <h4 className="text-sm font-semibold">Month status summary</h4>
                    <div className="relative shrink-0" ref={monthSummaryInfoBtnRef}>
                      <button
                        type="button"
                        onClick={() => {
                          setShowDayActionMenu(false);
                          setShowDayActionsInfo(false);
                          setShowMonthSummaryInfo((prev) => !prev);
                        }}
                        className={`rounded p-1 text-[hsl(var(--ds-muted-fg))] ${
                          darkMode
                            ? "hover:bg-[hsl(var(--ds-muted))]"
                            : "hover:bg-[hsl(var(--ds-muted))]"
                        }`}
                        aria-label="Month status summary details"
                        aria-expanded={showMonthSummaryInfo}
                        title="Info"
                      >
                        <DsIcon icon={Info} size="sm" />
                      </button>
                      {showMonthSummaryInfo &&
                        typeof document !== "undefined" &&
                        createPortal(
                          <div
                            data-month-summary-info
                            role="tooltip"
                            className={`fixed z-[10040] overflow-y-auto rounded-md border p-3 text-left text-xs leading-snug shadow-lg ${
                              darkMode
                                ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-muted-fg))]"
                                : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-muted-fg))]"
                            }`}
                            style={{
                              top: monthSummaryInfoCoords.top,
                              left: monthSummaryInfoCoords.left,
                              width: monthSummaryInfoCoords.width || 288,
                              maxHeight: monthSummaryInfoCoords.maxHeight || 220,
                            }}
                          >
                            {monthStatusSummaryNote}
                          </div>,
                          document.body
                        )}
                    </div>
                  </div>
                  {showFinalisedMark && (
                    <DsBadge tone="success">Finalised</DsBadge>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-1.5">
                  {processMonthStatusSummary.entries.map(({ code, displayCode, count }) => {
                    const pres = getStatusColorPresentation(displayCode, codeColorMap, darkMode);
                    return (
                      <div
                        key={`proc-status-${code}`}
                        className={`rounded border px-2 py-1.5 ${
                          darkMode
                            ? "border-[hsl(var(--ds-border))]"
                            : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
                        }`}
                      >
                        <p
                          className={`text-[10px] uppercase font-semibold ${pres.className}`}
                          style={pres.style}
                        >
                          {displayCode}
                        </p>
                        <p className="text-sm font-semibold tabular-nums">
                          {formatStatusCount(count)}
                        </p>
                      </div>
                    );
                  })}
                  <div
                    className={`rounded border px-2 py-1.5 col-span-3 ${
                      darkMode
                        ? "border-[hsl(var(--ds-primary))]/40 bg-[hsl(var(--ds-primary))]/10"
                        : "border-[hsl(var(--ds-primary))]/30 bg-[hsl(var(--ds-primary))]/5"
                    }`}
                  >
                    <p className="text-[10px] uppercase text-[hsl(var(--ds-muted-fg))]">
                      Payable days
                    </p>
                    <p className="text-sm font-bold tabular-nums text-[hsl(var(--ds-primary))]">
                      {formatStatusCount(processMonthStatusSummary.payDays)}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        {!embedded && (
          <div
            className={`shrink-0 px-[calc(1rem*0.95)] py-[calc(0.75rem*0.95)] border-t flex items-center justify-end gap-[calc(0.5rem*0.95)] ${
              darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
            }`}
          >
            <button
              type="button"
              onClick={handleCancelForm}
              className="px-4 py-1.5 rounded-md text-sm font-semibold bg-red-600 text-white hover:bg-red-700 transition-colors shadow-sm"
            >
              Close
            </button>
          </div>
        )}
      </div>
      <RegulariseChecksRequestModal
        isOpen={showRegularizationModal}
        onClose={() => setShowRegularizationModal(false)}
        darkMode={darkMode}
        employeeId={employee?.id}
        employeeName={employee?.name || employee?.user?.name || employee?.employeeName || "Employee"}
        initialDateIso={selectedDateIso ? clampDateIsoToToday(selectedDateIso) : null}
        loggedInEmployeeId={loggedInEmployeeId}
        onSubmit={handleRegulariseChecksSubmit}
        zIndexClass="z-[11050]"
      />
      {showStatusEditModal && (
        <div className={`fixed inset-0 bg-black/50 flex items-center justify-center z-[11050] ${phoneEss ? PHONE_SHEET_BACKDROP_CLS : ""}`}>
          <div
            className={`w-[min(87.4vw,399px)] rounded-lg shadow-xl p-[calc(1rem*0.95)] ${phoneEss ? `max-md:w-full max-md:!p-4 ${PHONE_SHEET_PANEL_CLS}` : ""} ${
              darkMode ? "bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]" : "bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]"
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold">
                {statusEditHalfDayMode
                  ? statusEditRequiresWorkflow
                    ? `Apply for half-day status - Day ${selectedDay}`
                    : `Half-day edit - Day ${selectedDay}`
                  : statusEditRequiresWorkflow
                  ? `Apply for status change - Day ${selectedDay}`
                  : `Edit attendance status - Day ${selectedDay}`}
              </h4>
              <button
                type="button"
                onClick={() => setShowStatusEditModal(false)}
                className={`p-1 rounded ${darkMode ? "hover:bg-[hsl(var(--ds-muted))]" : "hover:bg-[hsl(var(--ds-muted))]"}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3">
              {!statusEditHalfDayMode ? (
                <div>
                  <label className="block text-xs font-medium mb-1">Day status</label>
                  <select
                    value={firstHalfStatus}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFirstHalfStatus(v);
                      setSecondHalfStatus(v);
                    }}
                    className={`w-full p-2 border rounded text-sm ${
                      darkMode ? "bg-[hsl(var(--ds-bg))] border-[hsl(var(--ds-border))]" : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"
                    }`}
                  >
                    {statusOptions.map((status) => (
                      <option key={`full-${status.code}`} value={status.code}>
                        {status.code} - {status.label}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <>
              <div>
                <label className="block text-xs font-medium mb-1">First half status</label>
                <select
                  value={firstHalfStatus}
                  onChange={(e) => setFirstHalfStatus(e.target.value)}
                  className={`w-full p-2 border rounded text-sm ${
                    darkMode ? "bg-[hsl(var(--ds-bg))] border-[hsl(var(--ds-border))]" : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"
                  }`}
                >
                  {statusOptions.map((status) => (
                    <option key={`fh-${status.code}`} value={status.code}>
                      {status.code} - {status.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Second half status</label>
                <select
                  value={secondHalfStatus}
                  onChange={(e) => setSecondHalfStatus(e.target.value)}
                  className={`w-full p-2 border rounded text-sm ${
                    darkMode ? "bg-[hsl(var(--ds-bg))] border-[hsl(var(--ds-border))]" : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"
                  }`}
                >
                  {statusOptions.map((status) => (
                    <option key={`sh-${status.code}`} value={status.code}>
                      {status.code} - {status.label}
                    </option>
                  ))}
                </select>
              </div>
                </>
              )}
              <div className={`rounded p-2 text-xs ${darkMode ? "bg-[#1F1F1F]" : "bg-[hsl(var(--ds-muted))]"}`}>
                Result: <span className="font-semibold">{combineStatuses(firstHalfStatus, secondHalfStatus)}</span>
              </div>
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowStatusEditModal(false)}
                className={`px-3 py-1.5 rounded text-sm ${phoneEss ? "max-md:min-h-[44px] max-md:flex-1 max-md:rounded-xl" : ""} ${
                  darkMode ? "bg-[hsl(var(--ds-muted))] hover:bg-[hsl(var(--ds-muted))]" : "bg-[hsl(var(--ds-muted))] hover:bg-[hsl(var(--ds-muted))]"
                }`}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isSavingStatus}
                onClick={handleSaveEditedStatus}
                className={`${phoneEss ? "max-md:min-h-[44px] max-md:flex-[2] max-md:rounded-xl max-md:justify-center" : ""} px-3 py-1.5 rounded text-sm inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45 hover:opacity-95 flex items-center`}
              >
                {isSavingStatus ? (
                  <span className="inline-flex items-center gap-1">Saving...</span>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-1" />
                    {statusEditRequiresWorkflow ? "Submit" : "Save"}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeAttendanceCalendarPopup;
